import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { BackendSourceOptions, DiscoveryItemClickEvent, DiscoveryItemImpressionEvent } from '@udemy/browse-event-tracking';
import { TrackImpression, Tracker } from '@udemy/event-tracking';
import { useMatchMedia } from '@udemy/hooks';
import { formatRoundNumber, useI18n } from '@udemy/i18n';
import { SpotIllustration } from '@udemy/illustrations';
import { Carousel } from '@udemy/react-structure-components';
import { getRequestData } from '@udemy/shared-utils';
import { mediaQueryPxToRem } from '@udemy/styles';
import { useUDData } from '@udemy/ud-data';
import { learnerCountForCategoryByCountry, categoryInfo, categoryModuleAllowedCountries, getHeadingForNewCategoriesModule } from '../utils/constants';
import styles from "./enhanced-categories.module.css";
export const EnhancedCategoriesComponent = () => {
  var _udConfig$price_count, _udConfig$price_count2;
  const requestData = getRequestData();
  const {
    gettext,
    interpolate
  } = useI18n();
  const isMobile = useMatchMedia(`(max-width: ${mediaQueryPxToRem(600)}rem)`);
  const isMin601 = useMatchMedia(`(min-width: ${mediaQueryPxToRem(601)}rem)`);
  const isMax980 = useMatchMedia(`(max-width: ${mediaQueryPxToRem(980)}rem)`);
  const isTablet = isMin601 && isMax980;
  const isDesktop = useMatchMedia(`(min-width: ${mediaQueryPxToRem(981)}rem)`);
  const {
    Config: udConfig
  } = useUDData();
  const priceCountryId = (_udConfig$price_count = (_udConfig$price_count2 = udConfig.price_country) === null || _udConfig$price_count2 === void 0 ? void 0 : _udConfig$price_count2.id) !== null && _udConfig$price_count !== void 0 ? _udConfig$price_count : '';
  const renderCard = useCallback((category, index, isTablet, isDesktop) => {
    const categoryDetails = categoryInfo(gettext)[category.id];
    const clientEvent = {
      id: category.id,
      type: 'course_category',
      trackingId: String(category.id),
      serveTrackingId: String(category.id),
      backendSource: BackendSourceOptions.DISCOVERY,
      position: index,
      uiRegion: 'popular_categories_enhanced',
      badgeFamilies: null,
      relatedSourceId: null,
      relatedSourceType: null
    };
    const trackImpression = function () {
      Tracker.publishEvent(new DiscoveryItemImpressionEvent(clientEvent));
    };
    const handleCategoryClick = function () {
      Tracker.publishEvent(new DiscoveryItemClickEvent(clientEvent));
    };
    return /*#__PURE__*/React.createElement(TrackImpression, {
      trackFunc: trackImpression,
      key: category.id
    }, /*#__PURE__*/React.createElement("a", {
      className: classNames('ud-custom-focus-visible'),
      href: `/courses/${categoryDetails.formatted}/`,
      onClick: handleCategoryClick
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles[isDesktop ? `category-card-desktop-${index % 6 + 1}` : isTablet ? `category-card-tablet-${index % 6 + 1}` : `category-card-mobile-${index % 6 + 1}`])
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['category-text-div'])
    }, /*#__PURE__*/React.createElement("span", {
      className: classNames(isDesktop ? 'ud-heading-xl' : isTablet ? 'ud-heading-lg' : 'ud-heading-sm', styles['category-card-text'])
    }, categoryDetails.translated), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
      className: classNames(isDesktop ? 'ud-text-md' : isTablet ? 'ud-text-sm' : 'ud-text-xs', styles['category-card-text'])
    }, interpolate(gettext('%(learnersCount)s learners'), {
      learnersCount: formatRoundNumber(category.learnersCount, 0, requestData.locale, {
        notation: 'compact',
        roundingMode: 'ceil'
      })
    }, true)))), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles[isDesktop ? `category-card-layout-desktop-${index % 6 + 1}` : isTablet ? `category-card-layout-tablet-${index % 6 + 1}` : `category-card-layout-mobile-${index % 6 + 1}`]),
      id: 'category-' + categoryDetails.formatted
    }, /*#__PURE__*/React.createElement(SpotIllustration, {
      name: categoryDetails.illustration,
      alt: categoryDetails.translated,
      height: isMobile ? 91 : isTablet ? 120 : 150,
      width: isMobile ? 90 : isTablet ? 120 : 150,
      className: classNames(styles[isMobile ? 'spot-illustration-image-mobile' : isTablet ? 'spot-illustration-image-tablet' : 'spot-illustration-image'])
    })))));
  }, [gettext, interpolate, isMobile, requestData.locale]);
  const content = useMemo(() => {
    var _learnerCountForCateg;
    return (((_learnerCountForCateg = learnerCountForCategoryByCountry()) === null || _learnerCountForCateg === void 0 ? void 0 : _learnerCountForCateg[priceCountryId]) || []).map((category, index) => renderCard(category, index, isTablet, isDesktop));
  }, [priceCountryId, renderCard, isTablet, isDesktop]);
  return categoryModuleAllowedCountries.has(priceCountryId) ? /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['top-categories'])
  }, /*#__PURE__*/React.createElement("span", {
    className: classNames('ud-heading-serif-xxl', styles['section-title'])
  }, getHeadingForNewCategoriesModule(gettext, priceCountryId)), /*#__PURE__*/React.createElement("section", {
    className: classNames('ud-layout-5-course-card-row', styles['top-margin-md'])
  }, /*#__PURE__*/React.createElement(Carousel, {
    className: classNames('carousel-5-course-cards-content-area', {
      [styles['grid-gap-sm']]: !!isMobile
    }),
    pageByFullWidth: true,
    showPager: !isMobile && !isTablet,
    allowScroll: isMobile || isTablet
  }, content))) : /*#__PURE__*/React.createElement(React.Fragment, null);
};
//# sourceMappingURL=enhanced-categories.react-component.js.map